// src/components/common/SplashScreen.tsx
import React, { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Building2 } from 'lucide-react';

interface SplashScreenProps {
  onComplete: () => void;
  duration?: number;
}

const BUSINESS_QUOTES = [
  "Building the future of commerce.",
  "Every big business starts small.",
  "Your business, simplified.",
  "Empowering local businesses.",
  "Success is a journey, not a destination.",
];

export const SplashScreen: React.FC<SplashScreenProps> = ({ 
  onComplete, 
  duration = 2500 
}) => {
  const [quote] = useState(() => BUSINESS_QUOTES[Math.floor(Math.random() * BUSINESS_QUOTES.length)]);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Start fade out slightly before completion
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, duration - 400);

    const completeTimer = setTimeout(() => {
      onComplete();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [duration, onComplete]);

  return (
    <div 
      className={cn(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-light-bg dark:bg-dark-bg transition-opacity duration-400",
        fadeOut ? "opacity-0" : "opacity-100"
      )}
    >
      <div className="flex flex-col items-center animate-fade-in">
        {/* Logo Animation */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-primary/20 dark:bg-golden/20 rounded-full blur-xl animate-pulse-slow"></div>
          <div className="relative w-24 h-24 bg-white dark:bg-dark-card rounded-2xl shadow-xl flex items-center justify-center border border-light-border dark:border-dark-border">
            <Building2 className="w-12 h-12 text-primary dark:text-golden" />
          </div>
        </div>
        
        <h1 className="text-3xl font-bold text-light-text dark:text-dark-text tracking-tight mb-3">
          Majengo
        </h1>
        
        <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle italic max-w-xs text-center">
          "{quote}"
        </p>
      </div>

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-1">
        <div className="w-2 h-2 rounded-full bg-primary/30 dark:bg-golden/30 animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 rounded-full bg-primary/60 dark:bg-golden/60 animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 rounded-full bg-primary dark:bg-golden animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
};
